import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import {
  PageHeading,
  Tabs,
  CardBalance,
  Button,
  Section,
  Empty,
  Text,
  Icon,
} from 'barte-design-system';
import { LockIcon } from '@phosphor-icons/react';
import {
  BolsaoStatusPill,
  useBolsaoAjudaIcon,
  BolsaoComoFunciona,
  type BolsaoStatus,
} from '../bolsao/bolsao';
// Reaproveita todo o CSS de card da página principal
import '../DetalheLojista/DetalheLojista.css';
import './DetalheLojistaSoBolsao.css';

/**
 * Variação para discussão: mantém o box de saldo atual ao lado do nome do
 * lojista e coloca apenas o card do bolsão na faixa nova. Serve para mostrar
 * o card do bolsão solto, sem as outras quebras ao lado dele.
 */


type TabKey =
  | 'visao-geral'
  | 'socios'
  | 'financeiro'
  | 'garantias'
  | 'taxas'
  | 'relatorios'
  | 'parceiros'
  | 'integracao';

const LOJISTA = {
  nome: 'Loja da Maria Moda',
  cnpj: '43.812.905/0001-27',
  razaoSocial: 'MARIA MODA COMERCIO DE VESTUARIO LTDA',
  nomeFantasia: 'Loja da Maria Moda',
  nomeResponsavel: 'Maria Aparecida Fontes',
  dataNascimento: '14/03/1987',
  dataAbertura: '02/08/2019',
  categoria: 'VESTUARIO',
  site: 'https://lojadamariamoda.com.br',
  cpfResponsavel: '318.774.260-15',
  documentosInternacionais: 'Não',
  cnaePrincipal: '4781-4/00',
  tier: 'Ativo sem antecipação',
};

/** Box de saldo atual: total = disponível + a liberar. O bolsão fica fora. */
const SALDO_TOTAL = 'R$ 160.000,00';
const SALDO_DISPONIVEL = 'R$ 40.000,00';
const SALDO_A_LIBERAR = 'R$ 120.000,00';

const BOLSAO_RETIDO = 2450;
/** Teto opcional na config: null = sem teto (trava integral). */
const BOLSAO_TETO: number | null = 4000;
const BOLSAO_PERCENTUAL_TPV = '2,0% do TPV';
/** Nao existe montante esperado: o alvo do bolsao e o proprio teto. */
const BOLSAO_FALTA_PARA_TETO =
  BOLSAO_TETO === null ? null : Math.max(BOLSAO_TETO - BOLSAO_RETIDO, 0);

const BOLSAO_STATUS_ATUAL: BolsaoStatus = 'retendo';

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function DetalheLojistaSoBolsao() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('visao-geral');



  const bolsaoIcon = useBolsaoAjudaIcon(BOLSAO_TETO);

  // ── Box de saldo atual (o que existe hoje no cabeçalho) ───────────────────

  const boxSaldoAtual = (
    <div className="saldo-legado">
      <div className="saldo-legado-total">
        <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
          Saldo total
        </Text>
        <Text variant="body-xl" style={{ fontWeight: 700 }}>
          {SALDO_TOTAL}
        </Text>
      </div>
      <div className="saldo-legado-linhas">
        <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
          Disponível {SALDO_DISPONIVEL}
        </Text>
        <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
          A liberar {SALDO_A_LIBERAR}
        </Text>
      </div>
      <Button variant="fill" size="sm" onClick={() => undefined}>
        Solicitar Saque
      </Button>
    </div>
  );

  // ── Faixa nova: só o card do bolsão ───────────────────────────────────────

  const faixaSoBolsao = (
    <div className="dl-cards-row" data-cards={3}>
      <div className="dl-card-wrapper">
        <CardBalance className="dl-card dl-card--bolsao">
          <CardBalance.Info
            variant="big"
            title="Bolsão"
            balance={formatBRL(BOLSAO_RETIDO)}
            icon={bolsaoIcon}
            showPills
            pills={
              <BolsaoStatusPill status={BOLSAO_STATUS_ATUAL} size="sm" />
            }
            rightSlot={
              <div className="card-action">
                <Button
                  variant="outline"
                  size="sm"
                  round="full"
                  onClick={() => setActiveTab('garantias')}
                >
                  Ver detalhes
                </Button>
              </div>
            }
          />
        </CardBalance>
      </div>
    </div>
  );

  const abaVisaoGeral = (
    <>
      {faixaSoBolsao}

      <Section
        title="Informações da empresa"
        variant="two-columns"
        headerActions={
          <Button variant="outline" size="sm" onClick={() => undefined}>
            Editar
          </Button>
        }
        secondColumn={
          <>
            <Section.Row label="Data de abertura" content={LOJISTA.dataAbertura} />
            <Section.Row label="Categoria" content={LOJISTA.categoria} />
            <Section.Row label="Site" content={LOJISTA.site} />
            <Section.Row label="CPF responsável" content={LOJISTA.cpfResponsavel} />
            <Section.Row
              label="Permitir documentos internacionais"
              content={LOJISTA.documentosInternacionais}
            />
            <Section.Row label="CNAE principal" content={LOJISTA.cnaePrincipal} />
          </>
        }
      >
        <Section.Row label="CNPJ" content={LOJISTA.cnpj} />
        <Section.Row label="Razão social" content={LOJISTA.razaoSocial} />
        <Section.Row label="Nome fantasia" content={LOJISTA.nomeFantasia} />
        <Section.Row label="Nome responsável" content={LOJISTA.nomeResponsavel} />
        <Section.Row label="Data de nascimento" content={LOJISTA.dataNascimento} />
      </Section>

      <Section
        title="Tier"
        headerActions={
          <Button variant="outline" size="sm" onClick={() => undefined}>
            Editar
          </Button>
        }
      >
        <Section.Row label="Tier" content={LOJISTA.tier} />
      </Section>
    </>
  );

  // ── Aba Garantias (igual à da página principal) ───────────────────────────

  const abaGarantias = (
    <div className="garantias-tab">
      <div className="garantias-header">
        <div className="garantias-titulo">
          <Icon icon={LockIcon} size={18} />
          <Text variant="body-lg" style={{ fontWeight: 600 }}>
            Bolsão
          </Text>
        </div>
        <Button variant="outline" size="sm" onClick={() => undefined}>
          Editar configuração
        </Button>
      </div>

      <CardBalance className="dl-card dl-card--detalhe">
        <div className="bolsao-metrics">
          <div className="bolsao-metric">
            <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
              % TPV
            </Text>
            <Text variant="body-lg" style={{ fontWeight: 600 }}>
              {BOLSAO_PERCENTUAL_TPV}
            </Text>
          </div>
          <div className="bolsao-metric">
            <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
              Teto
            </Text>
            <Text variant="body-lg" style={{ fontWeight: 600 }}>
              {BOLSAO_TETO === null ? 'Sem teto' : `R$ ${formatBRL(BOLSAO_TETO)}`}
            </Text>
          </div>
          <div className="bolsao-metrics-divider" />
          <div className="bolsao-metric">
            <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
              Status
            </Text>
            <div className="bolsao-metric-pill">
              <BolsaoStatusPill status={BOLSAO_STATUS_ATUAL} size="base" />
            </div>
          </div>
          <div className="bolsao-metric">
            <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
              Retido
            </Text>
            <Text variant="body-lg" style={{ fontWeight: 600 }}>
              R$ {formatBRL(BOLSAO_RETIDO)}
            </Text>
          </div>
          {/* Sem teto ou teto ja atingido: o campo nao acrescenta nada e sai do card. */}
          {BOLSAO_FALTA_PARA_TETO !== null && BOLSAO_FALTA_PARA_TETO > 0 && (
            <div className="bolsao-metric">
              <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
                Falta para o teto
              </Text>
              <Text variant="body-lg" style={{ fontWeight: 600 }}>
                R$ {formatBRL(BOLSAO_FALTA_PARA_TETO)}
              </Text>
            </div>
          )}
        </div>
      </CardBalance>

      <BolsaoComoFunciona teto={BOLSAO_TETO} />
    </div>
  );

  const abaForaDoEscopo = (
    <Empty description={'Conteúdo fora do escopo deste protótipo.'} showButton={false} />
  );

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'visao-geral', label: 'Visão geral' },
    { key: 'socios', label: 'Sócios' },
    { key: 'financeiro', label: 'Financeiro' },
    { key: 'garantias', label: 'Garantias' },
    { key: 'taxas', label: 'Taxas' },
    { key: 'relatorios', label: 'Relatórios' },
    { key: 'parceiros', label: 'Parceiros' },
    { key: 'integracao', label: 'Integração' },
  ];

  return (
    <PortalPayfacsLayout activeItem="detalhe-lojista-so-bolsao">
      <main className="content">
        <div className="content-inner detalhe-lojista detalhe-lojista--so-bolsao">
          <div className="header-com-saldo">
            <div className="header-com-saldo-titulo">
              <PageHeading title={LOJISTA.nome} onBack={() => navigate(-1)} />
            </div>
            {boxSaldoAtual}
          </div>

          <Tabs>
            {TABS.map((tab) => (
              <Tabs.Tab
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs>

          {activeTab === 'visao-geral' && abaVisaoGeral}
          {activeTab === 'garantias' && abaGarantias}
          {activeTab !== 'visao-geral' && activeTab !== 'garantias' && abaForaDoEscopo}
        </div>
      </main>
    </PortalPayfacsLayout>
  );
}
